import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { HardHat, Mail, Lock, User as UserIcon, ArrowRight, ShieldCheck } from "lucide-react";
import { SignupCardForm, SignupCardTrust, SignupCardSubmit } from "@/components/SignupCardForm";
const fmtErr = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ") || fallback;
  return detail?.msg || fallback;
};

export default function Auth() {
  const { user, setUser, login, loginWithEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const wantSignup = location.pathname === "/signup" || params.get("mode") === "signup";
  const [mode, setMode] = useState(wantSignup ? "signup" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const cardSubmitRef = useRef(null);

  useEffect(() => {
    if (user) {
      // Users without a card go through the onboarding gate before they see the dashboard.
      if (user.requires_card_setup && !user.is_platform_admin) navigate("/onboarding/card", { replace: true });
      else navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "signup" && !agree) {
      setErr("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }
    if (mode === "signup" && !cardReady) {
      setErr("Please finish entering your card details.");
      return;
    }
    if (mode === "signup" && form.password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }

    setBusy(true);
    try {
      if (mode === "signup") {
        // 1. Tokenize the card client-side (no PCI data ever hits our server).
        const paymentMethodId = cardSubmitRef.current ? await cardSubmitRef.current() : null;
        if (!paymentMethodId) { setBusy(false); return; }  // card component already surfaced the error
        // 2. Register + start trial atomically.
        const ref = localStorage.getItem("bidmatch_ref") || "";
        const { data } = await api.post("/auth/register", {
          email: form.email, password: form.password, name: form.name, ref,
          payment_method_id: paymentMethodId,
          plan: "growth", interval: "monthly",
        });
        setUser(data.user);
        navigate("/dashboard", { replace: true });
      } else {
        const u = await loginWithEmail(form.email, form.password);
        if (u?.requires_card_setup && !u?.is_platform_admin) navigate("/onboarding/card", { replace: true });
        else navigate("/dashboard", { replace: true });
      }
    } catch (e2) {
      setErr(fmtErr(e2?.response?.data?.detail, "Something went wrong. Please try again."));
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSubmit = mode === "login"
    ? !busy
    : !busy && agree && cardReady && form.email && form.password.length >= 8;

  return (
    <div className="min-h-screen bg-emerald-950 flex items-center justify-center p-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6 text-white">
          <div className="h-9 w-9 rounded-lg bg-lime-400/20 flex items-center justify-center">
            <HardHat className="h-5 w-5 text-lime-300" strokeWidth={2.2} />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight">BidMatchAI</span>
        </Link>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="grid grid-cols-2 text-sm font-medium">
            <button onClick={() => { setMode("login"); setErr(""); }} data-testid="tab-login"
              className={`py-4 transition-colors ${mode === "login" ? "bg-white text-slate-900" : "bg-slate-50 text-slate-400 hover:text-slate-600"}`}>Sign in</button>
            <button onClick={() => { setMode("signup"); setErr(""); }} data-testid="tab-signup"
              className={`py-4 transition-colors ${mode === "signup" ? "bg-white text-slate-900" : "bg-slate-50 text-slate-400 hover:text-slate-600"}`}>Create account</button>
          </div>

          <div className="px-8 py-7">
            <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="auth-heading">
              {mode === "signup" ? "Start your 5-day free trial" : "Welcome back"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {mode === "signup" ? "Card required · $50/mo after trial · Cancel anytime." : "Sign in to your BidMatchAI account."}
            </p>

            {mode === "signup" && (
              <div className="mt-4 rounded-lg bg-emerald-50/60 border border-emerald-200 px-3 py-2.5 text-xs text-emerald-800 inline-flex items-start gap-2" data-testid="signup-value-strip">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
                <span>You won&apos;t be charged today. Full access for 5 days. Cancel any time before then and pay $0.</span>
              </div>
            )}

            <form onSubmit={submit} className="mt-5 space-y-4">
              {mode === "signup" && (
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input value={form.name} onChange={set("name")} data-testid="auth-name" type="text" placeholder="Full name"
                    className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input value={form.email} onChange={set("email")} data-testid="auth-email" type="email" required placeholder="you@company.com"
                  className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input value={form.password} onChange={set("password")} data-testid="auth-password" type="password" required
                  placeholder={mode === "signup" ? "Create a password (min 8 characters)" : "Password"}
                  className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
              </div>

              {mode === "signup" && (
                <>
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Card details</p>
                    <SignupCardForm
                      email={form.email}
                      name={form.name}
                      disabled={busy}
                      onReadyChange={setCardReady}
                      onError={setErr}
                      submitRef={cardSubmitRef}
                    />
                    <SignupCardTrust />
                  </div>
                </>
              )}

              {err && <p className="text-sm text-red-600" data-testid="auth-error">{err}</p>}

              {mode === "signup" && (
                <label className="flex items-start gap-2.5 text-xs text-slate-500 cursor-pointer select-none" data-testid="auth-agree-label">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="auth-agree"
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500/40" />
                  <span>I agree to BidMatchAI&apos;s <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Terms of Service</a>, <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Privacy Policy</a>, and <a href="/disclaimer" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Disclaimer</a>. My card will be charged $50/mo when the 5-day trial ends unless I cancel.</span>
                </label>
              )}

              {mode === "login" && (
                <div className="text-right -mt-1">
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-slate-500 hover:text-emerald-700">Forgot password?</Link>
                </div>
              )}

              {mode === "signup" ? (
                <SignupCardSubmit busy={busy} disabled={!canSubmit}>Start free trial</SignupCardSubmit>
              ) : (
                <>
                  <button type="submit" disabled={busy} data-testid="auth-submit"
                    className="w-full bg-emerald-900 text-white rounded-xl py-3.5 text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2">
                    {busy ? "Please wait…" : <>Sign in <ArrowRight className="h-4 w-4" /></>}
                  </button>
                  <div className="relative py-1">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                    <div className="relative flex justify-center"><span className="bg-white px-3 text-xs uppercase tracking-wider text-slate-400">or</span></div>
                  </div>
                  <button type="button" onClick={login} disabled={busy} data-testid="auth-google"
                    className="w-full bg-white text-slate-700 rounded-xl py-3.5 text-sm font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2">
                    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 3.4 14.7 2.5 12 2.5 6.7 2.5 2.5 6.7 2.5 12S6.7 21.5 12 21.5c6.9 0 9.5-4.8 9.5-7.3 0-.5 0-.9-.1-1.3H12z"/></svg>
                    Continue with Google
                  </button>
                  <p className="text-[11px] text-slate-400 text-center -mt-1">Google sign-in is only for existing accounts. New signups use email + card above.</p>
                </>
              )}
            </form>
          </div>
        </div>

        <p className="text-center text-xs text-emerald-200/60 mt-5">
          {mode === "signup" ? "5-day free trial · $50/mo after · Cancel anytime, no charge." : "New here? Choose \"Create account\" above."}
        </p>
        <p className="text-center text-xs text-emerald-200/50 mt-2" data-testid="auth-legal-note">
          By continuing, you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-lime-300">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-lime-300">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
